/**
 * The confirm sheet — Turn 12's third phone frame, and the drawing is explicit about why it
 * exists at all: *"the confirm sheet exists on mobile even though desktop has a lighter confirm,
 * since a mis-tap on a phone is far likelier than a mis-click."*
 *
 * A SHEET, NOT A PAGE. The board stays behind it, dimmed — you can still see the round you were
 * looking at while you decide, which is the whole reason the drawing puts the search in a sheet
 * too. It repeats the clock, because on a phone the thing you lose is your place in the queue and
 * the pinned bar is now covered by this.
 *
 * CANCEL IS A SECOND CHOICE, NOT A SYMMETRICAL ONE. The drawing stacks a full-width blue Confirm
 * over a bordered white `Pick someone else` rather than putting them side by side. Two equal
 * buttons at the bottom of a phone is how somebody drafts the wrong running back with his thumb.
 *
 * The consequence line is the drawing's, verbatim in structure: what the man does to your roster,
 * then what confirming does to everybody else's screen — *"Confirming writes 4.08 for the whole
 * room — only a commissioner can clear it."* That second clause is 11b's asymmetry stated where
 * it actually bites.
 */

import { NflMark } from './NflChip.js';
import { useScrollLock } from './useScrollLock.js';
import { dock } from './dock.js';
import { PickClock } from './PickClock.js';
import { shortSurname, splitName } from './BoardGrid.js';

export interface PhoneConfirmProps {
  /** `4.08`, as drawn — the sheet names the pick on the button, not just in the header. */
  code: string;
  name: string;
  pos?: string;
  bye?: number | null;
  /** Where the ADP consensus had him. Absent when no ADP file is loaded. */
  adp?: string;
  /** What he does to your roster. Null when it cannot be worked out rather than guessed. */
  fills: string | null;
  /** The pick clock, or the elapsed time when no clock is set. Red at the drawn weight. */
  clock?: string;
  clockOver?: boolean;
  /** The same clock as one PickClock (17n): when the last pick landed and the allowance. */
  since?: string | null;
  seconds?: number | null;
  /** False when a commissioner is entering somebody else's pick — the sheet says whose. */
  yours: boolean;
  owner: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function PhoneConfirm(props: PhoneConfirmProps) {
  const { code, name, pos, bye, adp, fills, clock, clockOver, yours, owner, busy, since, seconds } = props;
  const { last, first } = splitName(name);

  // The board behind stays put while the sheet is up. It is dimmed on purpose — you are meant to
  // see where you were — but a swipe belongs to the sheet, not to the round underneath it.
  useScrollLock(true);

  /*
   * DOCKED, and of everything this fix touched this is the one that had to be right. It is the
   * pick confirmation on draft night: `position: fixed` over a board that is thousands of pixels
   * wide and tall, rendered inside a `main` that pull-to-refresh transforms — so the sheet a
   * manager taps Confirm on was being positioned against the board rather than against his
   * screen. See `src/ui/dock.tsx`.
   */
  return dock(
    <div className={`pcsheetwrap p17 ${yours ? '' : 'forother'}`} role="dialog" aria-modal="true"
      aria-label={`Confirm ${name} at ${code}`}>
      {/* Tapping the dimmed board behind is the same as "Pick someone else". */}
      <button type="button" className="pcscrim" aria-label="Close" onClick={props.onCancel} />
      <div className="pcsheet">
        <span className="pcgrab" aria-hidden="true" />

        <div className="pceyebrow">
          <span className={`pctag ${yours ? '' : 'forother'}`}>
            {yours ? 'Your pick' : `Entering for ${owner}`} · {code}
          </span>
          {since
            ? <PickClock since={since} seconds={seconds ?? null} size="s" skin="light" className="pcsheetclock" />
            : clock && <span className={`pcsheetclock ${clockOver ? 'over' : ''}`}>{clock}</span>}
        </div>
        <div className="pctop">
          <div className="pcwho">
            <b><span className="sur">{shortSurname(last)}</span>{first ? ` ${first}` : ''}</b>
            <div className="pcmeta">
              {pos && <span className={`poschip ident pos-${pos}`}>{pos}</span>}
              <NflMark player={name} />
              {bye ? <span className="mut">B{bye}</span> : null}
              {adp ? <span className="mut">ADP {adp}</span> : null}
            </div>
          </div>
        </div>

        <p className="pcsheetnote">
          {/*
            HONEST WHERE IT CANNOT TELL. The drawing's line is "Fills your open RB slot", which is
            a claim about the roster — so when the roster cannot be read (no ADP file, an unknown
            player, a commissioner entering for somebody else) the sheet drops the clause rather
            than inventing one. The half it always knows is the half that matters.
          */}
          {fills && <>{fills} </>}
          {yours
            ? <>Confirming writes {code} for the whole room, only a commissioner can clear it.</>
            : <>Confirming writes {code} for {owner}; it appears on every screen at once.</>}
        </p>

        <div className="pcsheetacts">
          <button type="button" className="btn primary pcgo" disabled={busy}
            onClick={props.onConfirm}>
            {busy ? 'Writing…' : 'Confirm pick'}
          </button>
          <button type="button" className="btn pcback" onClick={props.onCancel}>
            Pick someone else
          </button>
        </div>
      </div>
    </div>
  );
}
